// ExportJsonDialog — bekräftelse + format-toggle innan workflow JSON
// kopieras / laddas ner. "Export all"-checkboxen styr om
// `detailedDescription` (lång markdown från Detaljer-dialogen) +
// icon/subject/to/label följer med, eller om export blir basic-data
// (default — strukturer + kort `description`).
//
// Default off — basic-data är den säkra delningen. User opt-ar in
// medvetet på "all" när de vill dela med sig av detalj-text också.

import { useState, useMemo, useCallback, useEffect } from 'react';
import { Copy, Download, Check } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { copyToClipboard } from '@/lib/clipboard';
import { toLeanExport } from '@/lib/leanExport';
import type { WorkflowState, MultiSheetWorkflowState } from '@/types/workflow';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from '@/i18n/LanguageContext';

export interface ExportJsonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Snapshot-getter — kallas vid öppning och vid toggle av includeAll. */
  getState: () => WorkflowState | MultiSheetWorkflowState;
  /** Loggning av action — wired till useUserActions. */
  onLog?: (action: string, details: string) => void;
}

export function ExportJsonDialog({ open, onOpenChange, getState, onLog }: ExportJsonDialogProps) {
  const { t } = useTranslation();
  const [includeAll, setIncludeAll] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset checkbox till "off" varje gång dialogen öppnas — "Export all"
  // är ett medvetet val per export, inte ett sticky preference. Annars
  // skulle user kunna exportera detalj-text omedvetet flera dagar senare.
  useEffect(() => {
    if (open) {
      setIncludeAll(false);
      setCopied(false);
    }
  }, [open]);

  // Snapshot:a state vid öppning så toggle inte plockar in mid-edit-data.
  const state = useMemo(
    () => (open ? getState() : null),
    [open, getState],
  );

  // Räkna fram JSON-strängen färskt vid varje toggle så preview-storleken
  // matchar det user faktiskt får i clipboard / nedladdad fil.
  const json = useMemo(() => {
    if (!state) return '';
    const lean = toLeanExport(state, { includeAll });
    return JSON.stringify(lean, null, 2);
  }, [state, includeAll]);

  const sizeKb = useMemo(() => Math.round((json.length / 1024) * 10) / 10, [json]);

  const handleCopy = useCallback(async () => {
    try {
      await copyToClipboard(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      onLog?.('JSON copied', includeAll ? 'lean+all' : 'lean basic');
      toast({ title: t('copied'), description: t('jsonCopied') });
    } catch {
      toast({ title: t('error'), description: t('couldNotCopy'), variant: 'destructive' });
    }
  }, [json, includeAll, onLog, t]);

  const handleDownload = useCallback(() => {
    if (!json) return;
    const stateName = (state && 'name' in state ? state.name : '') || 'workflow';
    const safeName = stateName.replace(/[^a-z0-9-_]+/gi, '_').slice(0, 60) || 'workflow';
    const suffix = includeAll ? '-all' : '';
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}${suffix}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    onLog?.('JSON downloaded', includeAll ? 'lean+all' : 'lean basic');
  }, [json, state, includeAll, onLog]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Exportera JSON</DialogTitle>
          <DialogDescription>
            Kopiera eller ladda ner workflow:et som JSON. Default är "basic
            data" — strukturen + korta block-summeringar. Detaljerade
            markdown-beskrivningar (från Detaljer-dialogen) tas bara med
            om du kryssar i "Export all".
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-3 rounded-md border bg-muted/30 p-3">
          <Checkbox
            id="export-all"
            checked={includeAll}
            onCheckedChange={(v) => setIncludeAll(v === true)}
            className="mt-0.5"
          />
          <div className="flex-1 min-w-0 space-y-1">
            <Label htmlFor="export-all" className="text-sm font-medium cursor-pointer">
              Export all
            </Label>
            <p className="text-xs text-muted-foreground">
              Inkludera <code className="text-[11px]">detailedDescription</code>,
              icon, subject, to + connector-labels. Kan innehålla intern
              dokumentation — kryssa bara i när du medvetet vill dela med
              dig av detalj-texten.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Storlek: ~{sizeKb} KB</span>
          <span>{includeAll ? 'Allt-läge' : 'Basic-läge (default)'}</span>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button variant="outline" onClick={handleDownload} className="gap-2">
            <Download className="w-4 h-4" /> Ladda ner
          </Button>
          <Button onClick={handleCopy} className="gap-2">
            {copied
              ? <><Check className="w-4 h-4" /> Kopierat!</>
              : <><Copy className="w-4 h-4" /> Kopiera</>
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
